import { createSlice } from '@reduxjs/toolkit';

import { readCustomBackgroundIndex, sweepOrphanedCustomBackgrounds } from '../services';
import { importCustomBackground } from './customBackgrounds.thunks';

import type { CustomBackgroundEntry } from '../services';
import type { BackgroundImageErrorCode } from '../utils';
import type { PayloadAction } from '@reduxjs/toolkit';

export type CustomBackgroundsState = {
  /** Index entries only, oldest first — never the data URLs. */
  items: CustomBackgroundEntry[];
  isImporting: boolean;
  importError: BackgroundImageErrorCode | null;
};

// Drop any bytes a tab left behind when it died mid-write, before we measure what's in use.
sweepOrphanedCustomBackgrounds();

/**
 * Read synchronously at module evaluation, the same way `userSlice` seeds `blurLevel` and
 * `backgroundImageId`. That's the point of storing these in `localStorage`: the selection and the
 * image it names become available on the same tick, so the first render can already apply the right
 * background instead of flashing the raw camera while an async store answers.
 *
 * Only the index goes in the store. The data URLs are ~165K characters each and would bloat both
 * memory and the Redux devtools; they're read on demand through the memoised accessors in
 * `webRTC/services`, which is cheap because `localStorage` is synchronous.
 */
export const initialCustomBackgroundsState: CustomBackgroundsState = {
  items: readCustomBackgroundIndex(),
  isImporting: false,
  importError: null,
};

export const customBackgroundsSlice = createSlice({
  name: 'customBackgrounds',
  initialState: initialCustomBackgroundsState,
  reducers: {
    /** A background was removed. The listener does the storage write and the knock-on shedding. */
    customBackgroundDeleted: (state, { payload: id }: PayloadAction<string>) => {
      state.items = state.items.filter((item) => item.id !== id);
    },
    /** Another tab rewrote storage; re-seed from what's actually there. */
    customBackgroundsSynced: (state, { payload }: PayloadAction<CustomBackgroundEntry[]>) => {
      state.items = payload;
    },
    importErrorDismissed: (state) => {
      state.importError = null;
    },
    /**
     * The user tried to add an image while at the cap. Carries no state — the picker already shows
     * why — and exists purely so the listener can report it. Without a dispatch here the cap would be
     * enforced silently and `custom_limit_reached` would never fire, since the storage guard behind it
     * is only reachable once something has already got past the UI.
     */
    customBackgroundLimitReached: () => {},
  },
  extraReducers: (builder) => {
    builder
      .addCase(importCustomBackground.pending, (state) => {
        state.isImporting = true;
        state.importError = null;
      })
      .addCase(importCustomBackground.fulfilled, (state, { payload }) => {
        state.isImporting = false;
        state.items.push(payload);
      })
      .addCase(importCustomBackground.rejected, (state, { payload }) => {
        state.isImporting = false;
        // `payload` is the typed code from `rejectWithValue`; anything else is a genuine crash.
        state.importError = payload ?? 'storageFailed';
      });
  },
});

export const { customBackgroundDeleted, customBackgroundLimitReached, customBackgroundsSynced, importErrorDismissed } =
  customBackgroundsSlice.actions;

export const customBackgroundsReducer = customBackgroundsSlice.reducer;
